import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { DownloadSimple, MagnifyingGlass, Trophy, CheckCircle, Clock } from "@phosphor-icons/react";

/**
 * ReferralsPanel — Admin tab for the referral programme.
 *
 * Sub-tabs:
 *   1. Ledger — every (referrer, referred) pair with sign-up + qualifying
 *      dates and a bonus-granted indicator. Search by either email.
 *   2. Leaderboard — top referrers by qualifying-referrals count.
 *
 * The CSV export downloads the full ledger via a signed backend endpoint.
 */
export default function ReferralsPanel() {
  const [sub, setSub] = useState("ledger");
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all"); // all | pending | qualified
  const [ledger, setLedger] = useState({ items: [], total: 0, loading: true });
  const [leaderboard, setLeaderboard] = useState({ items: [], loading: true });

  const loadLedger = async () => {
    setLedger((l) => ({ ...l, loading: true }));
    try {
      const { data } = await api.get("/admin/referrals", { params: { q, status, limit: 200 } });
      setLedger({ items: data.items || [], total: data.total || 0, loading: false });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load referrals");
      setLedger({ items: [], total: 0, loading: false });
    }
  };

  const loadLeaderboard = async () => {
    setLeaderboard((l) => ({ ...l, loading: true }));
    try {
      const { data } = await api.get("/admin/referrals/leaderboard", { params: { limit: 50 } });
      setLeaderboard({ items: data.items || [], loading: false });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load leaderboard");
      setLeaderboard({ items: [], loading: false });
    }
  };

  useEffect(() => {
    if (sub === "ledger") loadLedger();
    else loadLeaderboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sub, status]);

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/referrals/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `referrals-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (e) {
      toast.error("Export failed");
    }
  };

  const stats = useMemo(() => {
    const qualified = ledger.items.filter((r) => !!r.qualified_at).length;
    const pending = ledger.items.length - qualified;
    return { qualified, pending };
  }, [ledger.items]);

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <h2 className="font-display font-black text-2xl text-rf-ink">Referrals</h2>
          <p className="text-sm text-rf-ink-soft mt-1">
            {ledger.total} total signups via referral · {stats.qualified} qualifying (added their first recipe) · {stats.pending} pending
          </p>
        </div>
        <button data-testid="referrals-export-csv" onClick={exportCsv} className="btn-outline-ink !py-2 !px-4 text-sm inline-flex items-center gap-2">
          <DownloadSimple size={16} weight="bold" /> Export CSV
        </button>
      </div>

      <div className="flex gap-2 mb-6">
        <button
          data-testid="referrals-tab-ledger"
          onClick={() => setSub("ledger")}
          className={`px-4 py-2 rounded-full text-sm font-bold ${sub === "ledger" ? "bg-rf-ink text-rf-cream" : "bg-white text-rf-ink-soft border border-rf-ink/10"}`}
        >
          Ledger
        </button>
        <button
          data-testid="referrals-tab-leaderboard"
          onClick={() => setSub("leaderboard")}
          className={`px-4 py-2 rounded-full text-sm font-bold ${sub === "leaderboard" ? "bg-rf-ink text-rf-cream" : "bg-white text-rf-ink-soft border border-rf-ink/10"}`}
        >
          <Trophy size={14} weight="bold" className="inline mr-1 -mt-0.5" /> Leaderboard
        </button>
      </div>

      {sub === "ledger" ? (
        <div>
          <div className="flex gap-3 mb-4 flex-wrap">
            <div className="relative flex-1 min-w-[220px]">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-rf-ink-soft" />
              <input
                data-testid="referrals-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && loadLedger()}
                placeholder="Search referrer or referred email"
                className="input-rf w-full !pl-9"
              />
            </div>
            <select
              data-testid="referrals-status-filter"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="input-rf max-w-[200px]"
            >
              <option value="all">All statuses</option>
              <option value="qualified">Qualified only</option>
              <option value="pending">Pending only</option>
            </select>
            <button onClick={loadLedger} className="btn-outline-ink !py-2 !px-4 text-sm">Search</button>
          </div>

          {ledger.loading ? (
            <p className="text-rf-ink-soft py-8 text-center">Loading…</p>
          ) : ledger.items.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-3xl border border-rf-ink/5">
              <p className="text-rf-ink-soft">No referrals yet.</p>
              <p className="text-xs text-rf-ink-soft mt-2">Once users start sharing their links, they&apos;ll appear here.</p>
            </div>
          ) : (
            <div className="bg-white rounded-3xl border border-rf-ink/5 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="referrals-table">
                  <thead>
                    <tr className="text-left text-[11px] font-black uppercase tracking-widest text-rf-ink-soft bg-rf-cream-alt">
                      <th className="px-4 py-3">Referrer</th>
                      <th className="px-4 py-3">Referred user</th>
                      <th className="px-4 py-3">Signed up</th>
                      <th className="px-4 py-3">Qualified</th>
                      <th className="px-4 py-3">Bonus</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ledger.items.map((r) => (
                      <tr key={r.referral_id} className="border-t border-rf-ink/5">
                        <td className="px-4 py-3 font-bold text-rf-ink truncate">{r.referrer_email}</td>
                        <td className="px-4 py-3 text-rf-ink-soft truncate">{r.referred_email}</td>
                        <td className="px-4 py-3 text-rf-ink-soft whitespace-nowrap">{r.signed_up_at ? new Date(r.signed_up_at).toLocaleDateString() : "—"}</td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {r.qualified_at ? (
                            <span className="inline-flex items-center gap-1 text-emerald-700 font-bold">
                              <CheckCircle size={14} weight="fill" />
                              {new Date(r.qualified_at).toLocaleDateString()}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-rf-ink-soft">
                              <Clock size={14} weight="bold" /> Pending
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {r.bonus_granted ? (
                            <span className="chip !bg-emerald-500/20 !text-emerald-800">+5 granted</span>
                          ) : r.qualified_at ? (
                            <span className="chip !bg-rf-cream !text-rf-ink-soft">Cap reached</span>
                          ) : (
                            <span className="chip !bg-rf-cream !text-rf-ink-soft">Not yet</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div>
          {leaderboard.loading ? (
            <p className="text-rf-ink-soft py-8 text-center">Loading…</p>
          ) : leaderboard.items.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-3xl border border-rf-ink/5">
              <p className="text-rf-ink-soft">No qualifying referrers yet.</p>
            </div>
          ) : (
            <ol className="bg-white rounded-3xl border border-rf-ink/5 overflow-hidden" data-testid="referrals-leaderboard">
              {leaderboard.items.map((u, i) => (
                <li key={u.user_id} className="flex items-center gap-4 px-4 py-3 border-b border-rf-ink/5 last:border-b-0">
                  <span className={`text-2xl font-display font-black w-10 shrink-0 ${i === 0 ? "text-rf-orange" : i < 3 ? "text-rf-ink" : "text-rf-ink-soft"}`}>
                    {i + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-rf-ink truncate">{u.username || u.name || u.email}</p>
                    <p className="text-xs text-rf-ink-soft truncate">{u.email} · code {u.referral_code}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-display font-black text-xl text-rf-ink leading-none">{u.qualifying_referrals_count}</p>
                    <p className="text-[10px] uppercase tracking-widest text-rf-ink-soft">Qualifying</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-display font-black text-xl text-rf-orange leading-none">+{u.referral_bonus_recipes}</p>
                    <p className="text-[10px] uppercase tracking-widest text-rf-ink-soft">Bonus slots</p>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
    </div>
  );
}
